import range from 'lodash/range';

export const MAX_VISIBLE_PAGES_BUTTONS = 7;

export const calculateLastPage = (total: number, itemsPerPage: number): number => Math.ceil(total / itemsPerPage);

type InputPaginationWithEllipsis = {
  selectedPage: number;
  lastPage: number;
  maxVisiblePageButtons?: number;
};

type PaginationWithEllipsis = {
  paginationRange: number[];
  isEllipsis: (pos: number) => boolean;
};

export const getPaginationWithEllipsis = ({
  selectedPage,
  lastPage,
  maxVisiblePageButtons = MAX_VISIBLE_PAGES_BUTTONS,
}: InputPaginationWithEllipsis): PaginationWithEllipsis => {
  // max/min values will be taken in the calculation of the first and last ellipsis
  // (it makes no sense to have them before 2 or after last page - 1)
  // these values (2 or last page -1) will be used as if they could be ellipsis positions,
  // but ellipsis wont be shown in place of 2 or last page - 1,
  // as these values can be shown instead

  // if there are ellipsis in both sides of selected page then
  // selected page should be shown in the middle (maxVisiblePageButtons is odd):
  // 1, ellipsis, x more links in left, the selected page, x more links in right, ellipsis and last page
  // in this case, x = (maxVisiblePageButtons - 5)/2
  // and the first ellipsis would be before selected page - x:
  let firstEllipsis = Math.max(selectedPage - Math.floor((maxVisiblePageButtons - 5) / 2) - 1, 2);
  // Math.floor is taken so maxVisiblePageButtons can also be even

  // last ellipsis will be in the position after first ellipsis + 2x + 1:
  const lastEllipsis = Math.min(firstEllipsis + (maxVisiblePageButtons - 5) + 2, lastPage - 1);

  // if its last pagination, there is a possible adjust:
  // there will be 1 less ellipsis and possibly less elements so far because of last page
  // for example: if selected page is 9 and maxVisibleButtons is 7, we would have 1 ... 8 9 10 but we could show more links
  // so if it is in the last pagination, i.e., lastEllipsis = lastPage - 1, first ellipsis will be
  // in position last page - (maxVisibleButtons - 2)
  if (lastEllipsis === lastPage - 1) {
    firstEllipsis = Math.max(lastPage - (maxVisiblePageButtons - 2), 2);
  }
  const paginationRange = [1, ...range(firstEllipsis, lastEllipsis + 1), lastPage];

  const isEllipsis = (position: number): boolean =>
    (position === 1 && firstEllipsis !== 2) ||
    (position === paginationRange.length - 2 && lastEllipsis !== lastPage - 1);

  return { paginationRange, isEllipsis };
};
